import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const OrganizationContext = createContext();

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
};

export const OrganizationProvider = ({ children }) => {
  const { user, authenticatedFetch } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;

  // Clear state when user changes (logout or user switch)
  useEffect(() => {
    setOrganizations([]);
    setSelectedOrganization(null);
    localStorage.removeItem('selectedOrganizationId');
  }, [user?.id]);

  const loadOrganizations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/organisations`);

      if (!response.ok) {
        throw new Error('Failed to load organizations');
      }

      const data = await response.json();
      setOrganizations(data);
    } catch (err) {
      console.error('Error loading organizations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectOrganization = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    if (org) {
      setSelectedOrganization(org);
      localStorage.setItem('selectedOrganizationId', orgId);
    }
  };

  const isOwner = (orgId) => {
    if (!user || !orgId) return false;
    const org = organizations.find(o => o.id === orgId);
    return org?.ownerId === user.id;
  };

  const refreshOrganizations = async () => {
    await loadOrganizations();
  };

  const createOrganization = async (name) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`${API_URL}/api/organisations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create organization');
      }

      const newOrg = await response.json();
      setOrganizations(prev => [...prev, newOrg]);
      setSelectedOrganization(newOrg);
      localStorage.setItem('selectedOrganizationId', newOrg.id);
      return newOrg;
    } catch (err) {
      console.error('Error creating organization:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const renameOrganization = async (orgId, newName) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`${API_URL}/api/organisations/${orgId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newName })
      });

      if (response.status === 403) {
        throw new Error('Only organization owners can rename organizations');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to rename organization');
      }

      const updatedOrg = await response.json();
      setOrganizations(prev => prev.map(org => org.id === orgId ? updatedOrg : org));
      if (selectedOrganization?.id === orgId) {
        setSelectedOrganization(updatedOrg);
      }
      return updatedOrg;
    } catch (err) {
      console.error('Error renaming organization:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteOrganization = async (orgId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`${API_URL}/api/organisations/${orgId}`, {
        method: 'DELETE'
      });

      if (response.status === 403) {
        throw new Error('Only organization owners can delete organizations');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete organization');
      }

      setOrganizations(prev => prev.filter(org => org.id !== orgId));

      // If deleted org was selected, select another org
      if (selectedOrganization?.id === orgId) {
        const remainingOrgs = organizations.filter(org => org.id !== orgId);
        if (remainingOrgs.length > 0) {
          setSelectedOrganization(remainingOrgs[0]);
          localStorage.setItem('selectedOrganizationId', remainingOrgs[0].id);
        } else {
          setSelectedOrganization(null);
          localStorage.removeItem('selectedOrganizationId');
        }
      }
    } catch (err) {
      console.error('Error deleting organization:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadOrganizations();
    }
  }, [user]);

  useEffect(() => {
    if (organizations.length > 0) {
      const savedOrgId = localStorage.getItem('selectedOrganizationId');

      if (savedOrgId) {
        const savedOrg = organizations.find(o => o.id === savedOrgId);
        if (savedOrg) {
          setSelectedOrganization(savedOrg);
          return;
        }
      }

      setSelectedOrganization(organizations[0]);
      localStorage.setItem('selectedOrganizationId', organizations[0].id);
    }
  }, [organizations]);

  const value = {
    organizations,
    selectedOrganization,
    loading,
    error,
    loadOrganizations,
    selectOrganization,
    isOwner,
    refreshOrganizations,
    createOrganization,
    renameOrganization,
    deleteOrganization
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};
